import { useMemo } from 'react'
import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Response order: Strongly Agree → Agree → Neutral/Unsure → Disagree → Strongly Disagree (case-insensitive)
// Note: Data has inconsistent neutral labels: "Neutral / Unsure", "Neutral /Unsure", "Neutral Unsure"
const AGREE_RESPONSE_ORDER = ['strongly agree', 'somewhat agree', 'agree', 'neutral / unsure', 'neutral /unsure', 'neutral unsure', 'unsure', 'neutral', 'disagree', 'somewhat disagree', 'strongly disagree']

// Sort responses according to agree/disagree order (Unsure in the middle) - case-insensitive matching
const sortAgreeResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = AGREE_RESPONSE_ORDER.indexOf(a.label.toLowerCase())
    const bIdx = AGREE_RESPONSE_ORDER.indexOf(b.label.toLowerCase())
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

// Legend labels
const LEGEND_LABELS = ['Strongly Agree', 'Somewhat Agree', 'Neutral / Unsure', 'Somewhat Disagree', 'Strongly Disagree']

// Q12 reasons NOT to create - verified against topline report
const REASONS_NOT_QUESTIONS = [
  { id: 'Disagree_Bureaucracy', label: 'Increased bureaucracy and red tape interferes with local businesses and economic development.' },
  { id: 'Disagree_FirstNationsControl', label: 'Protected areas are an excuse for First Nations to take control of our lands and resources - it is not about conservation.' },
  { id: 'Disagree_EconOverConservation', label: 'Economic development and jobs should always be a priority over environmental conservation.' },
  { id: 'Disagree_EnoughParks', label: 'There are already enough parks—we should focus on managing the ones we have.' },
  { id: 'Disagree_EnvGroupPower', label: 'Protected areas give too much power to environmental groups and unelected decision-makers.' },
  { id: 'Disagree_LimitHuntFish', label: 'New parks limit opportunities for hunting and fishing.' },
  { id: 'Disagree_TourismImpact', label: 'Parks just attract more tourists & development, which makes the problem worse.' },
  { id: 'Disagree_VanityProject', label: 'Parks and protected areas are just another political vanity project catering to rich people and urban elites.' },
  { id: 'Disagree_Elitism', label: 'Protected areas are for out-of-touch urban elites; rural communities do NOT want more protected areas.' },
  { id: 'Disagree_LoseFreedom', label: 'Parks and protected areas deprive local residents of freedom and quality of life.' },
  { id: 'Disagree_EnoughProtection', label: 'Nature in my province is healthy enough, it does not need more protection.' },
] as const

export function ReasonsNotToCreate() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Sort questions by agreement level (Strongly Agree + Somewhat Agree) - highest first
  const sortedQuestions = useMemo(() => {
    if (!data) return REASONS_NOT_QUESTIONS

    const totalFilterKey = buildFilterKey('total', 'total')

    return [...REASONS_NOT_QUESTIONS].sort((a, b) => {
      const aData = data.questions[a.id]?.data[totalFilterKey]
      const bData = data.questions[b.id]?.data[totalFilterKey]

      const getAgreeScore = (qData: typeof aData) => {
        if (!qData) return 0
        const stronglyAgree = qData.responses.find(r => r.label === 'Strongly Agree' || r.label === 'Strongly agree')?.percentage || 0
        const somewhatAgree = qData.responses.find(r => r.label === 'Somewhat Agree' || r.label === 'Somewhat agree' || r.label === 'Agree')?.percentage || 0
        return stronglyAgree + somewhatAgree
      }

      return getAgreeScore(bData) - getAgreeScore(aData)
    })
  }, [data])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Reasons Not to Create Protected Areas</h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">Break: {breakName}</span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Please say if you AGREE or DISAGREE with the following reasons NOT to create new provincial parks and protected areas in your province.
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {LEGEND_LABELS.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('likert5', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            sortedQuestions.map(({ id, label }) => {
              const question = data.questions[id]
              if (!question) return null

              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{label}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                          {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortAgreeResponses(row.data!.responses)}
                            scaleType="likert5"
                            height={isBreakActive ? 24 : 28}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
